<template>
  <div
    class="tw-h-full tw-relative tw-group game-tile-background-color game-tile-background-color-hover tw-outline-none game-tile-radius game-tile-border"
    :class="Classes.GAME_TILE"
    tabindex="-1"
  >
    <base-link
      class="tw-flex tw-flex-col tw-h-full"
      :to="to"
      :link-primary="false"
      no-spacing
    >
      <div class="tw-relative tw-aspect-video">
        <item-card-stack v-if="isWebCollection" class="-tw-z-2" />
        <picture>
          <source
            v-if="large"
            media="(min-width: 1412px)"
            :srcset="item.logo.thumb_1280x720"
          />
          <img
            class="tw-absolute tw-size-full tw-object-cover tw-rounded-t-[calc(var(--tile-border-radius)-var(--tile-border-width))] tw-overflow-hidden"
            :class="[
              Classes.GAME_TILE_IMAGE,
              {
                'tw-rounded-b-[calc(var(--tile-border-radius)-var(--tile-border-width))]':
                  large && !smallScreen,
              },
            ]"
            :alt="item.name"
            :src="item.card_image"
            @load="imageLoaded = true"
            @error="(e) => (e.target.style.display = 'none')"
          />
        </picture>
        <!-- temp image as fallback while thumbnail renders -->
        <div
          v-if="!imageLoaded"
          class="tw-size-full tw-absolute tw-top-0 tw-left-0 tw-overflow-hidden tw-bg-theme-1 tw-z-1"
        >
          <font-awesome-icon
            class="tw-absolute tw-fill-current tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-text-5xl md:tw-text-6xl tw-opacity-30"
            icon="image"
          />
        </div>
        <div v-if="isWebCollection && adultContent" class="tw-absolute tw-m-2">
          <base-button
            auto-width
            xsmall
            small-padding
            danger
            class="tw-pointer-events-none"
            :class="Classes.TILE_TAG"
          >
            <span>
              {{ $t('adult') }}
            </span>
          </base-button>
        </div>
      </div>
      <div
        v-if="itemType === ITEM_TYPES.MOD && item.price && !modOwned(item.id)"
        class="tw-absolute tw-left-1 tw-top-1 tw-z-2"
      >
        <tooltip-hover
          fixed
          :text="`${$t('premium')} ${ugcName(game, true, true).value}`"
          background="primary"
        >
          <div
            class="tw-flex tw-justify-center tw-items-center tw-size-8 tw-text-black tw-rounded-md mod-gradient-premium"
          >
            <base-icon icon="diamond" class="tw-size-5" />
          </div>
        </tooltip-hover>
      </div>
      <div
        class="tw-px-4 tw-pt-4 tw-space-y-4"
        :class="[
          [ITEM_TYPES.GUIDE, ITEM_TYPES.COLLECTION].includes(itemType) ||
          !allowSubscribing ||
          (large && !smallScreen)
            ? 'tw-pb-4'
            : 'tw-pb-18',
          {
            'md:tw-absolute md:tw-bottom-0 md:tw-left-0 tw-z-2 md:tw-w-full tw-text-white':
              large && !smallScreen,
          },
        ]"
      >
        <div
          v-if="large"
          class="tw-hidden md:tw-block -tw-z-1 mod-gradient-bt--black tw-h-44 tw-w-full tw-absolute tw-inset-x-0 tw-bottom-0 tw-rounded-b-[calc(var(--tile-border-radius)-var(--tile-border-width))]"
        />
        <div class="tw-space-y-2">
          <p
            class="tw-text-h5 tw-font-bold tw-h-10 tw-leading-[1.1] tw-flex tw-items-center"
            :class="{
              'tw-text-danger-text': item.status === STATUS_DELETED,
              'tw-text-warning-text': item.status === STATUS_NOT_ACCEPTED,
            }"
          >
            <tooltip-hover
              v-if="item.visible === VISIBLE_HIDDEN"
              :text="$t('hidden')"
              background="info"
            >
              <font-awesome-icon
                class="tw-opacity-60 tw-translate-y-px tw-text-info"
                icon="eye-slash"
                size="sm"
              />
            </tooltip-hover>
            <tooltip-hover
              v-if="item.status === STATUS_NOT_ACCEPTED"
              :text="$t('pending')"
              background="warning"
            >
              <font-awesome-icon
                class="tw-translate-y-px tw-text-warning"
                icon="clock"
                size="sm"
              />
            </tooltip-hover>
            <tooltip-hover
              v-if="item.status === STATUS_DELETED"
              :text="$t('archived')"
              background="danger"
            >
              <font-awesome-icon
                class="tw-translate-y-px tw-text-danger"
                icon="trash"
                size="sm"
              />
            </tooltip-hover>
            <tooltip-hover
              v-if="itemType === ITEM_TYPES.COLLECTION && item.incomplete"
              :text="
                $t('collections_incomplete', {
                  ugc: ugcName(game, false, true).value,
                })
              "
              background="warning"
            >
              <font-awesome-icon
                class="tw-text-h6 tw-text-warning"
                icon="exclamation-triangle"
              />
            </tooltip-hover>
            <span
              class="tw-line-clamp-2"
              :class="[
                Classes.TILE_TITLE,
                {
                  'tw-text-warning tw-ml-2':
                    item.status === STATUS_NOT_ACCEPTED,
                  'tw-text-info tw-ml-2': item.visible === VISIBLE_HIDDEN,
                  'tw-text-danger tw-ml-2': item.status === STATUS_DELETED,
                  'tw-ml-6':
                    itemType === ITEM_TYPES.COLLECTION && item.incomplete,
                },
              ]"
            >
              {{ item.name }}
            </span>
          </p>
          <span
            class="tw-text-sm tw-leading-none tw-font-normal tw-flex tw-justify-between tw-w-full"
          >
            <span
              class="lg:tw-space-x-1 tw-truncate"
              :class="Classes.TILE_AUTHOR"
            >
              <span class="tw-opacity-70 tw-hidden lg:tw-inline-block">
                {{ $t('by') }}
              </span>
              <base-link
                class="tw-opacity-70 hover:tw-opacity-100 focus:tw-opacity-100"
                hover-primary
                :to="{
                  name:
                    itemType === ITEM_TYPES.MOD
                      ? USER_MOD_BROWSE_ROUTE
                      : itemType === ITEM_TYPES.COLLECTION
                        ? USER_COLLECTION_BROWSE_ROUTE
                        : USER_GUIDE_BROWSE_ROUTE,
                  params: { user: item.submitted_by.name_id.toLowerCase() },
                }"
              >
                {{ item.submitted_by.username }}
              </base-link>
            </span>
            <tooltip-hover
              v-if="!large || smallScreen"
              class="tw-whitespace-pre tw-ml-1"
              :class="Classes.TILE_UPDATED_DATE"
              :text="`${$t('added')}: ${humanDateFormat(
                item.date_live
              )}\n${$t('updated')}: ${timeSince(item.date_updated)} ${$t(
                'ago'
              )}`"
            >
              <div
                class="tw-flex tw-items-center tw-text-sm tw-leading-none tw-space-x-1 tw-opacity-70"
              >
                <font-awesome-icon
                  :class="Classes.DATE_ICON"
                  icon="sync-alt"
                  size="sm"
                />
                <span>{{ humanDateFormat(item.date_updated) }}</span>
              </div>
            </tooltip-hover>
          </span>
        </div>
        <template v-if="!large">
          <tooltip-hover :text="summaryOverflow ? item.summary : ''">
            <p
              ref="summaryEl"
              class="tw-text-sm tw-opacity-70 tw-leading-snug"
              :class="[
                Classes.TILE_SUMMARY,
                isWebCollection
                  ? 'tw-line-clamp-1 tw-break-all'
                  : 'tw-h-[60px] mod-util-truncate-three-lines',
              ]"
            >
              {{ item.summary }}
            </p>
          </tooltip-hover>
          <div
            class="tw-flex tw-items-center tw-gap-1 tw-h-6"
            :class="Classes.TILE_TAG_LIST"
          >
            <base-button
              v-if="tagName"
              auto-width
              xsmall
              small-padding
              primary
              truncate
              :read-only="!allowFiltering"
              :class="[
                Classes.TILE_TAG,
                'tw-capitalize',
                tags.length === 1
                  ? 'tw-max-w-full'
                  : tags.length === 2
                    ? 'tw-max-w-1/2'
                    : 'tw-max-w-[33.3%]',
              ]"
              @click.prevent="filterTag(tagName)"
            >
              <span>{{
                tagName.name_localized ||
                (tagName.key ? $t(tagName.key) : tagName.name)
              }}</span>
            </base-button>
            <base-button
              v-if="subTagName"
              auto-width
              xsmall
              small-padding
              primary
              truncate
              :read-only="!allowFiltering"
              :class="[
                Classes.TILE_TAG,
                'tw-capitalize',
                tags.length === 1
                  ? 'tw-max-w-full'
                  : tags.length === 2
                    ? 'tw-max-w-1/2'
                    : 'tw-max-w-[33.3%]',
              ]"
              @click.prevent="filterTag(subTagName)"
            >
              <span>{{
                subTagName.name_localized ||
                (subTagName.key ? $t(subTagName.key) : subTagName.name)
              }}</span>
            </base-button>
            <tooltip-hover
              v-if="tags.length > (subTagName ? 2 : 1)"
              class="tw-w-18 tw-hidden lg:tw-block"
              :text="
                tags
                  .map(({ name, localized }) => localized || name)
                  .filter(
                    (x) =>
                      ![tagName?.name_localized || tagName?.name].includes(x) &&
                      (!subTagName ||
                        ![
                          subTagName?.name_localized || subTagName?.name,
                        ].includes(x))
                  )
                  .join(', ')
              "
            >
              <span
                v-if="tags.length > (subTagName ? 2 : 1)"
                class="tw-text-right tw-text-nowrap tw-text-sm tw-font-medium"
              >
                + {{ tags.length - (subTagName ? 2 : 1) }} {{ $t('more') }}
              </span>
            </tooltip-hover>
          </div>
          <div class="tw-border-t tw-border-[var(--tile-text)] tw-opacity-30" />
        </template>
        <slot name="footer" />
        <follow-button
          v-if="isWebCollection && allowSubscribing"
          :custom-class="Classes.GAME_TILE_SUBSCRIBE_BUTTON"
          is-browse
          is-secondary
          full-width
          :game="game"
          :collection="item"
          @click.prevent
        />
      </div>
    </base-link>

    <subscribe-button
      v-if="itemType === ITEM_TYPES.MOD && allowSubscribing"
      class="tw-absolute tw-bottom-4"
      :class="
        !large || smallScreen ? 'tw-inset-x-4' : 'tw-z-2 tw-max-w-36 tw-right-4'
      "
      :custom-class="Classes.GAME_TILE_SUBSCRIBE_BUTTON"
      is-browse
      :mod="item"
      :game="game"
    />

    <purchased-label
      v-if="itemType === ITEM_TYPES.MOD && modOwned(item.id)"
      type="card"
    />

    <div v-if="pinned" class="tw-absolute tw-left-3 tw-top-3">
      <tooltip-hover :text="$t('pinned')">
        <font-awesome-icon
          icon="thumbtack"
          class="game-link-color tw-pointer-events-none tw-drop-shadow-md tw-filter tw--rotate-45 tw-transform"
        />
      </tooltip-hover>
    </div>
  </div>
</template>

<script>
import { KEYS_COLLECTIONS, KEYS_GUIDES, KEYS_MODS } from '@config/storeKeys.js'
import { GUIDE_FILTERS_TO_HIDE, GUIDE_PINNED_TAG } from '@config/guides.js'
import PurchasedLabel from '@shared/components/Misc/PurchasedLabel.vue'
import ItemCardStack from '@shared/components/List/ItemCardStack.vue'
import FollowButton from '@components/Collection/FollowButton.vue'
import SubscribeButton from '@components/Mod/SubscribeButton.vue'
import { containerHeight } from '@composables/useWindowEvents.js'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { humanDateFormat, timeSince } from '@helpers/utils.js'
import { purchasesStore, gameStore, store } from '@stores'
import { getTags } from '@helpers/localizedTags.js'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { computed, toRefs, ref } from 'vue'
import { Settings } from '@config/game.js'
import { BREAKPOINTS } from '@config'
import {
  USER_COLLECTION_BROWSE_ROUTE,
  USER_GUIDE_BROWSE_ROUTE,
  USER_MOD_BROWSE_ROUTE,
} from '@config/routeNames.js'
import {
  STATUS_NOT_ACCEPTED,
  STATUS_DELETED,
  VISIBLE_HIDDEN,
  ITEM_TYPES,
} from '@config/options.js'
import {
  useResourceValue,
  usePermissions,
  useFilter,
  useRoute,
  useTags,
} from '@composables'

export default {
  components: {
    SubscribeButton,
    PurchasedLabel,
    ItemCardStack,
    TooltipHover,
    FollowButton,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    to: {
      type: Object,
      required: true,
    },
    large: {
      type: Boolean,
      default: false,
    },
    itemType: {
      type: String,
      required: true,
      validation: (val) => Object.values(ITEM_TYPES).includes(val),
    },
    guideTags: {
      type: Object,
      default: undefined,
    },
  },
  setup(props) {
    const { item, itemType, guideTags } = toRefs(props)
    const { modOwned } = purchasesStore()
    const { getPublicGame } = gameStore()
    const { generateId } = store()

    const imageLoaded = ref(false)
    const summaryEl = ref(null)
    const userNameId = useRoute().getUserNameId().value
    const game = getPublicGame()
    const browseId = generateId(
      itemType.value === ITEM_TYPES.MOD
        ? KEYS_MODS
        : itemType.value === ITEM_TYPES.COLLECTION
          ? KEYS_COLLECTIONS
          : KEYS_GUIDES,
      `${game.value.name_id}${userNameId || ''}`
    )
    const smallScreen = useBreakpoints(BREAKPOINTS).smaller('md')
    const { filter, updateFilter } = useFilter(browseId)
    const { isGameModerator } = usePermissions()
    const { tagName, subTagName } = useTags(game, item, itemType)
    const { ugcName } = useResourceValue()

    const allowSubscribing = computed(() => Settings.value.allowSubscribing)
    const allowFiltering = computed(() => Settings.value.allowFiltering)
    const isWebCollection = computed(
      () => itemType.value === ITEM_TYPES.COLLECTION
    )

    const tags = computed(() => {
      if (guideTags.value) {
        const tagNames = guideTags.value.data.map((tag) =>
          tag.name.toLowerCase()
        )
        return item.value.tags.filter(
          (tag) =>
            tagNames.includes(tag.name.toLowerCase()) &&
            !GUIDE_FILTERS_TO_HIDE.includes(tag.name.toUpperCase())
        )
      }

      return isWebCollection.value
        ? getTags(
            game.value.collection_tag_options,
            isGameModerator.value,
            item
          )
        : getTags(game.value.tag_options, isGameModerator.value, item)
    })

    const adultContent = computed(() => item.value.maturity_option > 0)

    const pinned = computed(
      () =>
        itemType.value === ITEM_TYPES.GUIDE &&
        item.value.tags.some(
          (tag) => tag.name.toUpperCase() === GUIDE_PINNED_TAG
        )
    )

    const summaryOverflow = computed(
      () =>
        containerHeight.value &&
        summaryEl.value?.scrollHeight > summaryEl.value?.clientHeight
    )

    function filterTag(tag) {
      if (allowFiltering.value) {
        const _filter = filter.value.find((f) => f.tags.includes(tag.name))
        if (_filter) {
          updateFilter({ ..._filter, value: tag.name })
        }
      }
    }

    return {
      USER_COLLECTION_BROWSE_ROUTE,
      USER_GUIDE_BROWSE_ROUTE,
      USER_MOD_BROWSE_ROUTE,
      STATUS_NOT_ACCEPTED,
      allowSubscribing,
      isWebCollection,
      humanDateFormat,
      summaryOverflow,
      allowFiltering,
      STATUS_DELETED,
      VISIBLE_HIDDEN,
      adultContent,
      imageLoaded,
      smallScreen,
      ITEM_TYPES,
      subTagName,
      summaryEl,
      timeSince,
      filterTag,
      modOwned,
      tagName,
      Classes,
      ugcName,
      pinned,
      game,
      tags,
    }
  },
}
</script>
